'use strict';

import {ValidationErrorItem} from 'joi';

import {__} from '~/shared/src/utilities/messageCatalog';
import Joi from '~/shared/src/utilities/joiExtended';
import {panelSchema} from '~/shared/src/validationSchemes/panel';

import {
    store,
    StoreState
} from '~/frontend/src/store';
import {
    panelClasses,
    PanelType,
    PanelClass,
    PanelConfig
} from '~/frontend/src/components/base/page/PageTabPanel/definitions';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';


export const panelCreateNewItemSchema = Joi.object( {
    createNewItem: Joi.object( {
                          itemTypeId  : Joi.number()
                                           .description( 'The ID of the ItemType the item to created with.' ),
                          itemTypeName: Joi.string()
                                           .description( 'The Name of the ItemType the item to created with.' ),
                          key         : Joi.string()
                                           .description( 'The Key for the item to be created.' ),
                          name        : Joi.string()
                                           .description( 'The Name for the item to be created.' ),
                          statusId    : Joi.number()
                                           .description( 'The StatusId of the item to be created.' ),
                          mapping     : Joi.object( {
                                               id          : Joi.string()
                                                                .default( 'rootItemId' ),
                                               key         : Joi.string(),
                                               name        : Joi.string(),
                                               itemTypeId  : Joi.string(),
                                               itemTypeName: Joi.string()
                                           } )
                                           .description( 'The mapping for the new item attributes to the panel props. Note: The item id will be mapped to rootItemId by default.' )
                      } )
                      .or( 'itemTypeId', 'itemTypeName' )
                      .description( 'The configuration for creating a new item to open the panbel with.' )
} );


/**
 * Gets the tab index for the given panelKey
 *
 * @param {String} panelKey
 * @param {PanelProps[]} [openPanels]
 *
 * @return {number}
 *
 * @private
 */
export const getPanelIndex = ( panelKey : string, openPanels? : PanelProps[] ) : number | undefined => {
    if ( !openPanels ) {
        const state        = store.getState(),
              panelManager = state.panelManager;

        openPanels = panelManager.openPanels;
    }

    let retVal;

    for ( let i = 0; i < openPanels!.length; i++ ) {
        if ( openPanels![ i ].panelProps && panelKey === openPanels![ i ].panelProps.panelKey ) {
            retVal = i;
            break;
        }
    }

    return retVal;
}

export const getPanelKeyByPath = ( path : string, openPanels? : PanelProps[] ) : string | undefined => {
    if ( !openPanels ) {
        const state        = store.getState(),
              panelManager = state.panelManager;

        openPanels = panelManager.openPanels;
    }

    let retVal;

    if ( openPanels?.length ) {
        for ( let i = 0; i < openPanels.length; i++ ) {
            if ( path === openPanels[ i ].path ) {
                retVal = openPanels[ i ].panelProps.panelKey;
                break;
            }
        }
    }

    return retVal;
}

/**
 * Gets the index for the given panelKey
 *
 * @param {String} panelKey
 * @param {PanelProps[]} [openPopups]
 *
 * @return {number}
 *
 * @private
 */
export const getPopupIndex = ( panelKey : string, openPopups? : PanelProps[] ) : number | undefined => {
    if ( !openPopups ) {
        const state        = store.getState(),
              panelManager = state.panelManager;

        openPopups = panelManager.openPopups;
    }

    let retVal;

    for ( let i = 0; i < openPopups!.length; i++ ) {
        if ( openPopups![ i ].panelProps && panelKey === openPopups![ i ].panelProps.panelKey ) {
            retVal = i;
            break;
        }
    }

    return retVal;
}

/**
 * Returns the current panelProps for the given panelKey.
 *
 * @param {String} panelKey
 * @param {StoreState} state
 *
 * @return {PanelProps}
 */
export const getPanelProps = ( panelKey : string, state? : StoreState ) : PanelConfig => {
    state = state ?? store.getState();
    const panelManager = state.panelManager;

    let retVal : PanelConfig = {} as PanelConfig;

    if ( !panelKey.match( /^popup-/ ) ) {
        const panelIndex = getPanelIndex( panelKey, panelManager.openPanels );

        if ( panelIndex !== undefined && panelManager.openPanels[ panelIndex ] ) {
            retVal = panelManager.openPanels[ panelIndex ];
        }
    }
    else {
        const popupIndex = getPopupIndex( panelKey, panelManager.openPopups );

        if ( popupIndex !== undefined && panelManager.openPopups[ popupIndex ] ) {
            retVal = panelManager.openPopups[ popupIndex ];
        }
    }

    return retVal;
}

/**
 * Returns the current panelProps for the given subPanelKey.
 *
 * @param {String} subPanelKey
 * @param {StoreState} state
 *
 * @return {PanelProps}
 */
export const getSubPanelProps = ( subPanelKey : string, state? : StoreState ) : PanelProps => {
    state = state ?? store.getState();
    const panelManager = state.panelManager;

    let retVal = {};

    if ( panelManager ) {
        if ( !subPanelKey.match( /^popup-/ ) ) {
            for ( const openPanel of panelManager.openPanels ) {
                const openPanelKey = openPanel.panelProps.panelKey,
                      matches      = subPanelKey.match( `^${openPanelKey}_(.*)$` ),
                      subPanelPath = matches?.length === 2 ? matches[ 1 ] : undefined;

                if ( subPanelPath ) {
                    const subPanelPathParts = subPanelPath.split( '_' );

                    let panelProps = openPanel.panelProps;

                    for ( const subPanelPathPart of subPanelPathParts ) {
                        panelProps = panelProps?.subPanelProps?.[ subPanelPathPart ];
                    }

                    if ( panelProps ) {
                        retVal = panelProps;
                    }
                }
            }
        }
        else {
            for ( const openPopup of panelManager.openPopups ) {
                const openPanelKey = openPopup.panelProps.panelKey,
                      matches      = subPanelKey.match( `^${openPanelKey}_(.*)$` ),
                      subPanelPath = matches?.length === 2 ? matches[ 1 ] : undefined;

                if ( subPanelPath ) {
                    const subPanelPathParts = subPanelPath.split( '_' );

                    let panelProps = openPopup.panelProps;

                    for ( const subPanelPathPart of subPanelPathParts ) {
                        panelProps = panelProps?.subPanelProps?.[ subPanelPathPart ];
                    }

                    if ( panelProps ) {
                        retVal = panelProps;
                    }
                }
            }
        }
    }

    return retVal;
}

export const getPanelElusiveData = ( panelKey : string, state? : StoreState ) : PanelElusiveDataState | undefined => {
    state = state ?? store.getState();

    return state?.elusiveData?.[ panelKey ] as PanelElusiveDataState | undefined;
}
/**
 * Returns cumulated error data from all subPanels of a given panel.
 *
 * @param {String}     panelKey Key of the main tab panel
 * @param {StoreState} [state]  if not set, store.getState will be used
 *
 * @return {PanelErrorData}
 */
export const getSubPanelErrors = ( panelKey : string, state? : StoreState ) : { [ subPanelKey : string ] : ErrorData } => {
    state = state ?? store.getState();

    const retVal : { [ subPanelKey : string ] : ErrorData } = {},
          elusiveData                                       = state.elusiveData;

    if ( elusiveData ) {
        for ( const subPanelKey of Object.keys( elusiveData ) ) {
            if ( ( subPanelKey + '' ).startsWith( panelKey ) && elusiveData[ subPanelKey ]?.errorData ) {
                retVal[ subPanelKey + '' ] = elusiveData[ subPanelKey ]!.errorData;
            }
        }
    }

    return retVal;
}

export type ValidationError = {
    title : string;
    text : string;
    details : string[];
    panelProps : PanelProps
};

/**
 * Validates the given panelProps against panelSchema and checks if the class for given panelProps.panelType exists.
 *
 * @param {PanelProps} panelProps
 *
 * @return {true | string}
 */
export const preValidatePanelProps = ( panelProps : PanelProps ) : true | ValidationError => {
    const validationError : ValidationError = {
        title  : '',
        text   : '',
        details: [] as string[],
        panelProps
    };

    const validationResult = Joi.object()
                                .concat( panelSchema )
                                .concat( panelCreateNewItemSchema )
                                .concat( Joi.object( {
                                    panelType: Joi.string()
                                                  .valid( ...Object.keys( panelClasses ) )
                                } ) )
                                .validate(
                                    panelProps,
                                    {
                                        abortEarly  : false,
                                        convert     : false,
                                        noDefaults  : true,
                                        allowUnknown: true
                                    }
                                );

    if ( validationResult.error ) {
        validationError.title = __( 'YN.base.panelProps.validation.error.validationFails.title' );
        validationError.text = __( 'YN.base.panelProps.validation.error.validationFails.text' );
        validationResult.error.details.forEach( ( detail : ValidationErrorItem ) => {
            validationError.details.push( detail.message );
        } );
    }

    // error
    if ( validationError.title ) {
        return validationError;
    }
    else {
        const PanelComponent = panelClasses[ panelProps.panelType as PanelType ] as unknown as PanelClass;

        if ( !PanelComponent.onClosePanelAction ) {
            console.warn( `The panel component ${panelProps.panelType} doesn't provide a handler for closing (onClosePanelAction)!` );
        }

        if ( !PanelComponent.onSavePanelAction ) {
            console.warn( `The panel component ${panelProps.panelType} doesn't provide a handler for saving (onSavePanelAction)!` );
        }

        if ( !PanelComponent.buildPanelPathFromProps ) {
            console.warn( `The panel component ${panelProps.panelType} doesn't provide a handler for building the document.location path from props (buildPanelPathFromProps)!` );
        }

        if ( !PanelComponent.buildPanelPropsFromPath ) {
            console.warn( `The panel component ${panelProps.panelType} doesn't provide a handler for building the props from document.location path (buildPanelPropsFromPath)!` );
        }

        return true;
    }
}

/**
 * Validates the given panelProps against the panelPropsSchema.
 *
 * @param {Object} panelProps
 *
 * @return {true | }
 */
export const validatePanelProps = ( panelProps : PanelProps ) : true | ValidationError => {
    let retVal : true | ValidationError = true;

    const PanelComponent                    = panelClasses[ panelProps.panelType as PanelType ] as unknown as PanelClass,
          validationError : ValidationError = {
              title  : '',
              text   : '',
              details: [],
              panelProps
          };

    // is panelType instantiatable
    if ( PanelComponent ) {

        // check if panel configuration exists
        if ( !PanelComponent.panelPropsSchema ) {
            validationError.title = __( 'YN.base.panelProps.validation.error.noPanelConfigSchemaDefined.title' );
            validationError.text = __( 'YN.base.panelProps.validation.error.noPanelConfigSchemaDefined.text', { panelType: panelProps.panelType } );
        }

        // proceed if no error till here
        if ( !validationError.title ) {
            const validationResult = PanelComponent.panelPropsSchema.validate(
                panelProps,
                {
                    abortEarly: false,
                    convert   : false,
                    noDefaults: true
                }
            );

            if ( validationResult.error ) {
                validationError.title = __( 'YN.base.panelProps.validation.error.validationFails.title' );
                validationError.text = __( 'YN.base.panelProps.validation.error.validationFails.text' );
                validationResult.error.details.forEach( ( detail : ValidationErrorItem ) => {
                    validationError.details.push( detail.message );
                } );
            }
        }

        // error
        if ( validationError.title ) {
            retVal = validationError;
        }
    }

    return retVal;
}

export const preInitializePanelProps = ( panelProps : PanelProps ) : PanelProps => {
    const PanelComponent = panelClasses[ panelProps.panelType as PanelType ] as unknown as PanelClass;

    let retVal = panelProps;

    if ( PanelComponent.onPreInitializePanel ) {
        const user = store.getState().session?.user;

        if ( user ) {
            retVal = {
                ...panelProps,
                ...PanelComponent.onPreInitializePanel( {
                    ...panelProps,

                    // inject user
                    user
                } )
            };
        }
    }

    return retVal;
}

export const initializePanelProps = ( panelProps : PanelProps ) : PanelProps => {
    const PanelComponent = panelClasses[ panelProps.panelType as PanelType ] as unknown as PanelClass;

    let retVal = panelProps;

    if ( PanelComponent.onInitializePanel ) {
        const user = store.getState().session?.user;

        if ( user ) {
            retVal = {
                ...panelProps,
                ...PanelComponent.onInitializePanel( {
                    ...panelProps,

                    // inject user
                    user
                } )
            };
        }
    }

    return retVal;
}
